import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { io } from 'socket.io-client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FlashOn as FlashIcon,
  People as PeopleIcon,
  StopCircle as EndIcon,
  LeaderboardOutlined as LeaderboardIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

// Styled components following SongQ design system
const ManagementContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#9C27B0',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(156, 39, 176, 0.1)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#9C27B0',
  color: '#fff',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#7B1FA2',
  },
  '&.MuiButton-outlined': {
    borderColor: '#9C27B0',
    color: '#9C27B0',
    '&:hover': {
      backgroundColor: 'rgba(156, 39, 176, 0.1)',
    },
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? 'rgba(156, 39, 176, 0.2)' : 'rgba(158, 158, 158, 0.2)',
  color: status === 'active' ? '#9C27B0' : '#9E9E9E',
  border: `1px solid ${status === 'active' ? '#9C27B0' : '#9E9E9E'}`,
  fontWeight: 600,
}));

const FlashQuizManagement = forwardRef((props, ref) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [quizletRankingsDialogOpen, setQuizletRankingsDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [quizletRankings, setQuizletRankings] = useState([]);
  const [loadingQuizletRankings, setLoadingQuizletRankings] = useState(false);
  const [pushedQuizlets, setPushedQuizlets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [difficulties, setDifficulties] = useState([]);
  const [moods, setMoods] = useState([]);
  const [topics, setTopics] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  useImperativeHandle(ref, () => ({
    viewQuizDetails: (session) => {
      setSelectedSession(session);
      setViewDialogOpen(true);
    },
    editQuizSession: (session) => editQuizSession(session),
    viewParticipants: (session) => viewParticipants(session),
    viewQuizletRankings: (session) => viewQuizletRankings(session),
  }));

  // Form state - NO song selection for flash mode
  const [formData, setFormData] = useState({
    quiz_name: '',
    quiz_config: {
      difficulty_id: null,
      difficulty_ids: [],
      mood_ids: [],
      topic_ids: [],
      questions_per_song: 3
    },
    quiz_data: {
      questions: [],
      songs: []
    },
    rapid_config: {
      questions_per_song: 3,
      scoring_type: 'speed_bonus',
      show_correct_answer: true,
      leaderboard_update_frequency: 'real_time',
      delay_seconds: 10,
      song_timer_seconds: 60
    },
    scheduled_start_time: '',
    scheduled_end_time: ''
  });

  // Fetch difficulties
  const fetchDifficulties = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/config`);

      if (!response.ok) {
        throw new Error('Failed to fetch difficulties');
      }

      const config = await response.json();
      setDifficulties(config.difficulties || []);

      // Set default difficulty to first available if not set
      if (config.difficulties && config.difficulties.length > 0 && !formData.quiz_config.difficulty_id) {
        const defaultDifficultyId = config.difficulties[0].difficulty_id;
        setFormData(prev => ({
          ...prev,
          quiz_config: {
            ...prev.quiz_config,
            difficulty_id: defaultDifficultyId,
            difficulty_ids: [defaultDifficultyId]
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching difficulties:', err);
      enqueueSnackbar('Failed to fetch difficulties', { variant: 'error' });
    }
  }, [enqueueSnackbar, formData.quiz_config.difficulty_id]);

  // Fetch moods and topics from quiz lookups
  const fetchMoodsAndTopics = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const [moodRes, topicRes] = await Promise.all([
        fetch(`${baseUrl}/api/lookups/question-mood`),
        fetch(`${baseUrl}/api/lookups/question-topic`)
      ]);

      if (!moodRes.ok || !topicRes.ok) {
        throw new Error('Failed to fetch quiz lookups');
      }

      const [moodData, topicData] = await Promise.all([
        moodRes.json(),
        topicRes.json()
      ]);

      setMoods((moodData || []).filter(m => m.status === 1));
      setTopics((topicData || []).filter(t => t.status === 1));
    } catch (err) {
      console.error('Error fetching moods/topics:', err);
      enqueueSnackbar('Failed to fetch quiz lookups (moods/topics)', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Fetch flash quiz sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/flash/sessions`);

      if (!response.ok) {
        throw new Error('Failed to fetch flash quiz sessions');
      }

      const data = await response.json();
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching flash quiz sessions:', err);
      setError(err.message);
      enqueueSnackbar('Failed to fetch flash quiz sessions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Create flash quiz session
  const createSession = async () => {
    try {
      // Validate form data
      if (!formData.quiz_name.trim()) {
        enqueueSnackbar('Quiz name is required', { variant: 'error' });
        return;
      }

      // Convert datetime-local to ISO format for database
      const sessionData = {
        ...formData,
        quiz_type: 'flash',
        quiz_config: {
          ...formData.quiz_config,
          // If all difficulties selected, store sentinel "all" for compatibility
          difficulty_id:
            formData.quiz_config.difficulty_ids &&
            difficulties.length > 0 &&
            formData.quiz_config.difficulty_ids.length === difficulties.length
              ? 'all'
              : (formData.quiz_config.difficulty_ids?.[0] ||
                 formData.quiz_config.difficulty_id),
          difficulty_ids: formData.quiz_config.difficulty_ids || [],
          mood_ids: formData.quiz_config.mood_ids || [],
          topic_ids: formData.quiz_config.topic_ids || [],
          questions_per_song: formData.rapid_config.questions_per_song
        },
        quiz_data: {
          questions: [],
          songs: [] // No songs pre-selected for flash mode
        },
        rapid_config: {
          ...formData.rapid_config
        },
        scheduled_start_time: convertToISO(formData.scheduled_start_time),
        scheduled_end_time: convertToISO(formData.scheduled_end_time)
      };

      // console.log('Creating flash quiz session with data:', sessionData);

      const url = editingSession
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${editingSession.session_id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions`;

      const method = editingSession ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(sessionData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create flash quiz session');
      }

      if (editingSession) {
        enqueueSnackbar('Insta quiz session updated successfully!', { variant: 'success' });
        setEditDialogOpen(false);
      } else {
        enqueueSnackbar('Insta quiz session created successfully!', { variant: 'success' });
        setCreateDialogOpen(false);
      }
      resetForm();
      fetchSessions();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to create flash quiz session', { variant: 'error' });
    }
  };

  // Activate/Deactivate session
  const toggleSessionStatus = async (sessionId, currentStatus) => {
    try {
      const action = currentStatus === 'active' ? 'deactivate' : 'activate';
      // console.log(`[FlashQuizManagement] ${action} session ${sessionId}`);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/sessions/${sessionId}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      // console.log(`[FlashQuizManagement] ${action} response status:`, response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`[FlashQuizManagement] ${action} error:`, errorData);
        throw new Error(errorData.error || `Failed to ${action} session`);
      }

      await response.json();
      // console.log(`[FlashQuizManagement] ${action} success`);

      // Update session status immediately (WebSocket will also update it, but this provides instant feedback)
      setSessions(prev => prev.map(session => 
        session.session_id === sessionId
          ? { ...session, status: action === 'activate' ? 'active' : 'inactive' }
          : session
      ));

      const actionText = currentStatus === 'active' ? 'deactivated' : 'activated';
      enqueueSnackbar(`Session ${actionText} successfully!`, { variant: 'success' });
      // Don't need to fetchSessions here - WebSocket will handle the update
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to toggle session status', { variant: 'error' });
    }
  };

  // End flash quiz session
  const endFlashQuizSession = async (sessionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${sessionId}/end`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to end flash quiz session');
      }

      // Update session status immediately (WebSocket will also update it)
      setSessions(prev => prev.map(session => 
        session.session_id === sessionId
          ? { ...session, status: 'inactive', rapid_status: 'ended' }
          : session
      ));

      enqueueSnackbar('Insta quiz session ended!', { variant: 'success' });
      // Don't need to fetchSessions here - WebSocket will handle the update
    } catch (err) {
      enqueueSnackbar('Failed to end flash quiz session', { variant: 'error' });
    }
  };

  // Delete session
  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this flash quiz session?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete session');
      }

      enqueueSnackbar('Session deleted successfully!', { variant: 'success' });
      fetchSessions();
    } catch (err) {
      enqueueSnackbar('Failed to delete session', { variant: 'error' });
    }
  };

  // View quiz details
  const viewQuizDetails = (session) => {
    setSelectedSession(session);
    setViewDialogOpen(true);
  };

  // Convert ISO datetime to datetime-local format (YYYY-MM-DDTHH:mm)
  // Format in local timezone (not UTC) to match datetime-local input requirements
  const convertToDatetimeLocal = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      
      // Format in local timezone (not UTC)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error converting datetime:', error);
      return '';
    }
  };

  // Convert datetime-local format to ISO string
  const convertToISO = (datetimeLocal) => {
    if (!datetimeLocal) return null;
    try {
      // datetime-local format: YYYY-MM-DDTHH:mm
      // Convert to ISO format for database
      const date = new Date(datetimeLocal);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch (error) {
      console.error('Error converting to ISO:', error);
      return null;
    }
  };

  // Edit quiz session
  const editQuizSession = (session) => {
    setSelectedSession(session);
    setEditingSession(session);

    const config = typeof session.quiz_config === 'string'
      ? JSON.parse(session.quiz_config)
      : session.quiz_config;

    const rapidConfig = typeof session.rapid_config === 'string'
      ? JSON.parse(session.rapid_config)
      : session.rapid_config;

    setFormData({
      quiz_name: session.quiz_name,
      quiz_config: {
        difficulty_id: config.difficulty_id || config.difficulty,
        difficulty_ids: Array.isArray(config.difficulty_ids)
          ? config.difficulty_ids
          : (config.difficulty_id || config.difficulty ? [config.difficulty_id || config.difficulty] : []),
        mood_ids: Array.isArray(config.mood_ids) ? config.mood_ids : [],
        topic_ids: Array.isArray(config.topic_ids) ? config.topic_ids : [],
        questions_per_song: config.questions_per_song || 3
      },
      quiz_data: {
        questions: [],
        songs: []
      },
      rapid_config: {
        questions_per_song: rapidConfig.questions_per_song || 3,
        scoring_type: rapidConfig.scoring_type || 'speed_bonus',
        show_correct_answer: rapidConfig.show_correct_answer !== false,
        leaderboard_update_frequency: rapidConfig.leaderboard_update_frequency || 'real_time',
        delay_seconds: typeof rapidConfig.delay_seconds === 'number' ? rapidConfig.delay_seconds : 10,
        song_timer_seconds: typeof rapidConfig.song_timer_seconds === 'number' ? rapidConfig.song_timer_seconds : 60
      },
      scheduled_start_time: convertToDatetimeLocal(session.scheduled_start_time),
      scheduled_end_time: convertToDatetimeLocal(session.scheduled_end_time)
    });

    setEditDialogOpen(true);
  };

  // View participants
  const viewParticipants = async (session) => {
    try {
      setSelectedSession(session);
      setLoadingParticipants(true);
      setParticipants([]);
      setParticipantsDialogOpen(true);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/participants`);

      if (!response.ok) {
        throw new Error('Failed to fetch participants');
      }

      const data = await response.json();
      // console.log("data: ", data);
      if (data.success && Array.isArray(data.participants)) {
        setParticipants(data.participants.filter(p => p && typeof p === 'object'));
      } else {
        setParticipants([]);
      }
    } catch (err) {
      console.error('Error fetching participants:', err);
      enqueueSnackbar('Failed to fetch participants', { variant: 'error' });
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Fetch pushed quizlets to determine status
  const fetchPushedQuizlets = useCallback(async (sessionId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/flash/sessions/${sessionId}/pushed-quizlets`);
      if (!res.ok) return [];
      const data = await res.json();
      if (data.success && Array.isArray(data.items)) {
        const session = sessions.find(s => s.session_id === sessionId);
        const parsedRapid = (() => {
          const raw = session?.rapid_config;
          try { return typeof raw === 'string' ? JSON.parse(raw) : (raw || {}); } catch { return {}; }
        })();
        const songTimer = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;
        const items = data.items.map((it, idx) => {
          const deliveryMs = it.delivery_time ? new Date(it.delivery_time).getTime() : null;
          const expireAt = deliveryMs ? (deliveryMs + songTimer * 1000) : null;
          const now = Date.now();
          const isExpired = expireAt ? now > expireAt : false;
          const quizletId = it.id || `${it.song?.songs_master_id || 'song'}_${it.pushed_at}`;
          return {
            id: quizletId,
            song: it.song || {},
            pushed_at: it.pushed_at,
            delivery_time: it.delivery_time,
            expire_at: expireAt,
            status: idx === 0 && !isExpired ? 'running' : 'expired',
            isStopped: false // Will be updated based on whether it has users
          };
        });
        return items;
      }
      return [];
    } catch (e) {
      console.error('Error fetching pushed quizlets:', e);
      return [];
    }
  }, [sessions]);

  // View quizlet rankings
  const viewQuizletRankings = async (session) => {
    try {
      setSelectedSession(session);
      setLoadingQuizletRankings(true);
      setQuizletRankings([]);
      setPushedQuizlets([]);
      setQuizletRankingsDialogOpen(true);

      // Fetch both quizlet rankings and pushed quizlets
      const [rankingsRes, pushedItems] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/quizlet-rankings`),
        fetchPushedQuizlets(session.session_id)
      ]);

      if (!rankingsRes.ok) {
        throw new Error('Failed to fetch quizlet rankings');
      }

      const data = await rankingsRes.json();
      // console.log("Quizlet rankings data: ", data);
      
      let quizlets = [];
      if (data.success && Array.isArray(data.quizlets)) {
        quizlets = data.quizlets;
      }

      // Mark pushed quizlets that have users (not stopped)
      const updatedPushed = pushedItems.map(pq => {
        const hasUsers = quizlets.some(q => {
          const qId = q.question_id || `${q.song_id || ''}_${q.pushed_at || ''}`;
          return qId === pq.id && q.users && q.users.length > 0;
        });
        return { ...pq, isStopped: !hasUsers };
      });

      setPushedQuizlets(updatedPushed);

      // Filter out stopped quizlets (those without users or expired without stats)
      const filteredQuizlets = quizlets.filter(q => {
        const hasUsers = q.users && q.users.length > 0;
        if (!hasUsers) return false; // Don't show quizlets without users (stopped)
        return true;
      });

      setQuizletRankings(filteredQuizlets);
    } catch (err) {
      console.error('Error fetching quizlet rankings:', err);
      enqueueSnackbar('Failed to fetch quizlet rankings', { variant: 'error' });
      setQuizletRankings([]);
    } finally {
      setLoadingQuizletRankings(false);
    }
  };

  // Socket listeners for real-time participant updates
  const socketRef = useRef(null);
  const globalSocketRef = useRef(null);

  // Global socket for session list updates (status, participant count, etc.)
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const socket = io(baseUrl, { transports: ['websocket'] });
    globalSocketRef.current = socket;

    socket.on('connect', () => {
      // console.log('[FlashQuizManagement] Global socket connected');
      socket.emit('flash:join-global');
    });

    // Listen for session activation (auto-start)
    socket.on('flash:activated', async (payload) => {
      if (payload && payload.session_id) {
        const sid = payload.session_id;
        // console.log('[FlashQuizManagement] Session activated:', sid);
        // Optimistically mark active, and prefer showing the session name
        setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, status: 'active' } : s));
        const sessionName = (() => {
          const found = sessions.find(s => s.session_id === sid);
          return found?.quiz_name || `#${sid}`;
        })();
        enqueueSnackbar(`${sessionName} auto-activated`, { variant: 'info' });
      }
    });

    // Listen for session ended (auto-end)
    socket.on('flash:session_ended', async (payload) => {
      if (payload && payload.session_id) {
        const sid = payload.session_id;
        // console.log('[FlashQuizManagement] Session ended:', sid);
        setSessions(prev => prev.map(session => 
          session.session_id === sid
            ? { ...session, status: 'inactive', rapid_status: 'ended' }
            : session
        ));
        const sessionName = (() => {
          const found = sessions.find(s => s.session_id === sid);
          return found?.quiz_name || `#${sid}`;
        })();
        enqueueSnackbar(`${sessionName} ended`, { variant: 'info' });
      }
    });

    // Listen for leaderboard updates (updates participant counts)
    socket.on('flash:leaderboard', async (payload) => {
      if (payload && payload.session_id) {
        // console.log('[FlashQuizManagement] Leaderboard updated for session:', payload.session_id);
        // Update participant count for this session
        setSessions(prev => prev.map(session => 
          session.session_id === payload.session_id
            ? { 
                ...session, 
                participant_count: payload.leaderboard?.length || session.participant_count 
              }
            : session
        ));
      }
    });

    // Listen for participant updates
    socket.on('flash:participants', async (payload) => {
      if (payload && payload.session_id) {
        // console.log('[FlashQuizManagement] Participants updated for session:', payload.session_id);
        // Update participant count
        setSessions(prev => prev.map(session => 
          session.session_id === payload.session_id
            ? { 
                ...session, 
                participant_count: payload.participants?.length || session.participant_count 
              }
            : session
        ));
      }
    });

    // Listen for question pushed (updates rapid_status)
    socket.on('flash:question_pushed', async (payload) => {
      if (payload && payload.session_id) {
        const sid = payload.session_id;
        // console.log('[FlashQuizManagement] Question pushed for session:', sid);
        setSessions(prev => prev.map(session => 
          session.session_id === sid
            ? { ...session, rapid_status: 'question_active' }
            : session
        ));
        const sessionName = (() => {
          const found = sessions.find(s => s.session_id === sid);
          return found?.quiz_name || `#${sid}`;
        })();
        enqueueSnackbar(`Pushed question • ${sessionName}`, { variant: 'success' });
      }
    });

    // Listen for question stopped
    socket.on('flash:question_stopped', async (payload) => {
      if (payload && payload.session_id) {
        const sid = payload.session_id;
        // console.log('[FlashQuizManagement] Question stopped for session:', sid);
        setSessions(prev => prev.map(session => 
          session.session_id === sid
            ? { ...session, rapid_status: 'waiting' }
            : session
        ));
        const sessionName = (() => {
          const found = sessions.find(s => s.session_id === sid);
          return found?.quiz_name || `#${sid}`;
        })();
        enqueueSnackbar(`Stopped current question • ${sessionName}`, { variant: 'warning' });
      }
    });

    return () => {
      if (globalSocketRef.current) {
        globalSocketRef.current.emit('flash:leave-global');
        globalSocketRef.current.disconnect();
        globalSocketRef.current = null;
      }
    };
  }, [fetchSessions, enqueueSnackbar, sessions]);

  // Socket listeners for real-time participant updates in dialog
  useEffect(() => {
    if (!participantsDialogOpen || !selectedSession?.session_id) return;

    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const socket = io(baseUrl, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('flash:join', { sessionId: selectedSession.session_id });
    });

    socket.on('flash:leaderboard', async (payload) => {
      if (payload && payload.session_id === selectedSession.session_id) {
        // Refresh participants list to get updated scores
        try {
          const response = await fetch(`${baseUrl}/api/live-quiz/sessions/${selectedSession.session_id}/participants`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && Array.isArray(data.participants)) {
              setParticipants(data.participants.filter(p => p && typeof p === 'object'));
            }
          }
        } catch (err) {
          console.error('Error refreshing participants:', err);
        }
      }
    });

    socket.on('rapid:leaderboard', async (payload) => {
      if (payload && payload.session_id === selectedSession.session_id) {
        // Refresh participants list to get updated scores
        try {
          const response = await fetch(`${baseUrl}/api/live-quiz/sessions/${selectedSession.session_id}/participants`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && Array.isArray(data.participants)) {
              setParticipants(data.participants.filter(p => p && typeof p === 'object'));
            }
          }
        } catch (err) {
          console.error('Error refreshing participants:', err);
        }
      }
    });

    // Periodic refresh every 3 seconds
    const refreshInterval = setInterval(async () => {
      try {
        const response = await fetch(`${baseUrl}/api/live-quiz/sessions/${selectedSession.session_id}/participants`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.participants)) {
            setParticipants(data.participants.filter(p => p && typeof p === 'object'));
          }
        }
      } catch (err) {
        console.error('Error refreshing participants:', err);
      }
    }, 3000);

    return () => {
      clearInterval(refreshInterval);
      if (socketRef.current) {
        socketRef.current.emit('flash:leave', { sessionId: selectedSession.session_id });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [participantsDialogOpen, selectedSession?.session_id]);

  // Reset form
  const resetForm = () => {
    setFormData({
      quiz_name: '',
      quiz_config: {
        difficulty_id: difficulties.length > 0 ? difficulties[0].difficulty_id : null,
        difficulty_ids: difficulties.length > 0 ? [difficulties[0].difficulty_id] : [],
        mood_ids: [],
        topic_ids: [],
        questions_per_song: 3
      },
      quiz_data: {
        questions: [],
        songs: []
      },
      rapid_config: {
        questions_per_song: 3,
        scoring_type: 'speed_bonus',
        show_correct_answer: true,
        leaderboard_update_frequency: 'real_time',
        delay_seconds: 10,
        song_timer_seconds: 60
      },
      scheduled_start_time: '',
      scheduled_end_time: ''
    });
    setEditingSession(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  // Get rapid status color
  const getRapidStatusColor = (status) => {
    switch (status) {
      case 'waiting': return '#FFC107';
      case 'question_active': return '#4CAF50';
      case 'ended': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  // Load sessions on mount
  useEffect(() => {
    fetchSessions();
    fetchDifficulties();
    fetchMoodsAndTopics();
  }, [fetchSessions, fetchDifficulties, fetchMoodsAndTopics]);

  // Helpers for multi-select with "Select All"
  const areAllSelected = (selectedIds, options, idField) =>
    options.length > 0 && selectedIds.length === options.length &&
    options.every(opt => selectedIds.includes(opt[idField]));

  return (
    <ManagementContainer>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FlashIcon sx={{ fontSize: 32, color: '#9C27B0', mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ color: '#fff', fontWeight: 'bold' }}>
            Insta Quiz Management
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#B0B0B0', mb: 3 }}>
          Manage flash quiz sessions with on-demand question pushing and time-delayed delivery
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <ActionButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Insta Quiz
          </ActionButton>
          <ActionButton
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchSessions}
          >
            Refresh
          </ActionButton>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#9C27B0' }} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && sessions.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No flash quiz sessions found. Create your first flash quiz session to get started.
        </Alert>
      )}

      <Grid container spacing={3}>
        {sessions.map((session) => (
          <Grid item xs={12} md={6} lg={4} key={session.session_id}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {session.quiz_name}
                  </Typography>
                  <StatusChip
                    label={session.status}
                    status={session.status}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Status: ${session.rapid_status || 'waiting'}`}
                    size="small"
                    sx={{
                      backgroundColor: `${getRapidStatusColor(session.rapid_status)}20`,
                      color: getRapidStatusColor(session.rapid_status),
                      border: `1px solid ${getRapidStatusColor(session.rapid_status)}`,
                      mb: 1
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                    Participants: {session.participant_count || 0}
                  </Typography>
                  {session.scheduled_start_time && (
                    <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 0.5 }}>
                      Starts: {formatDate(session.scheduled_start_time)}
                    </Typography>
                  )}
                  {session.scheduled_end_time && (
                    <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>
                      Ends: {formatDate(session.scheduled_end_time)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#B0B0B0' }}>
                    Created: {formatDate(session.created_at)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => viewQuizDetails(session)}
                      sx={{ color: '#9C27B0' }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => editQuizSession(session)}
                      sx={{ color: '#4CAF50' }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="View Participants">
                    <IconButton
                      size="small"
                      onClick={() => viewParticipants(session)}
                      sx={{ color: '#2196F3' }}
                    >
                      <PeopleIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Quizlet Rankings">
                    <IconButton
                      size="small"
                      onClick={() => viewQuizletRankings(session)}
                      sx={{ color: '#FF9800' }}
                    >
                      <LeaderboardIcon />
                    </IconButton>
                  </Tooltip>

                  {session.status === 'active' && (
                    <Tooltip title="End Session">
                      <IconButton
                        size="small"
                        onClick={() => endFlashQuizSession(session.session_id)}
                        sx={{ color: '#F44336' }}
                      >
                        <EndIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={session.status === 'active' ? 'Deactivate' : 'Activate'}>
                    <IconButton
                      size="small"
                      onClick={() => toggleSessionStatus(session.session_id, session.status)}
                      sx={{ color: session.status === 'active' ? '#F44336' : '#4CAF50' }}
                    >
                      {session.status === 'active' ? <StopIcon /> : <PlayIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => deleteSession(session.session_id)}
                      sx={{ color: '#F44336' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{
          color: '#fff',
          borderBottom: '1px solid #333',
          backgroundColor: '#1A1A1A',
          fontSize: '1.5rem',
          fontWeight: 600,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FlashIcon sx={{ mr: 2, color: '#9C27B0' }} />
            {editingSession ? 'Edit Insta Quiz Session' : 'Create Insta Quiz Session'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          pt: 4,
          pb: 2,
          backgroundColor: '#1A1A1A',
          minHeight: '400px'
        }}>
          <Grid container spacing={3}>
            {/* Quiz Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quiz Name"
                value={formData.quiz_name}
                onChange={(e) => setFormData({ ...formData, quiz_name: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': { color: '#9C27B0' }
                  }
                }}
              />
            </Grid>

            {/* Difficulty (multi-select with Select All) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{
                  color: '#B0B0B0',
                  '&.Mui-focused': { color: '#9C27B0' }
                }}>
                  Difficulties
                </InputLabel>
                <Select
                  multiple
                  value={formData.quiz_config.difficulty_ids || []}
                  onChange={(e) => {
                    const { value } = e.target;
                    const allIds = difficulties.map(d => d.difficulty_id);

                    // If "Select All" clicked, toggle between all and none
                    if (value.includes('all')) {
                      const newSelected = areAllSelected(formData.quiz_config.difficulty_ids || [], difficulties, 'difficulty_id')
                        ? []
                        : allIds;

                      setFormData(prev => ({
                        ...prev,
                        quiz_config: {
                          ...prev.quiz_config,
                          difficulty_ids: newSelected,
                          difficulty_id: newSelected.length === allIds.length
                            ? 'all'
                            : (newSelected[0] || null)
                        }
                      }));
                    } else {
                      const selected = value;
                      setFormData(prev => ({
                        ...prev,
                        quiz_config: {
                          ...prev.quiz_config,
                          difficulty_ids: selected,
                          difficulty_id: selected.length === allIds.length
                            ? 'all'
                            : (selected[0] || null)
                        }
                      }));
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const diff = difficulties.find(d => d.difficulty_id === value);
                        return (
                          <Chip
                            key={value}
                            label={diff?.level_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9C27B0' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#9C27B0' },
                    '& .MuiSvgIcon-root': { color: '#B0B0B0' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1A1A1A',
                        border: '1px solid #333',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': { backgroundColor: '#333' },
                          '&.Mui-selected': {
                            backgroundColor: '#9C27B0',
                            '&:hover': { backgroundColor: '#7B1FA2' }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="all">
                    <em>{areAllSelected(formData.quiz_config.difficulty_ids || [], difficulties, 'difficulty_id') ? 'Deselect All' : 'Select All'}</em>
                  </MenuItem>
                  {difficulties.map((difficulty) => (
                    <MenuItem key={difficulty.difficulty_id} value={difficulty.difficulty_id}>
                      {difficulty.level_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Questions Per Song */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Questions Per Song"
                type="number"
                value={formData.rapid_config.questions_per_song}
                onChange={(e) => setFormData({
                  ...formData,
                  rapid_config: { ...formData.rapid_config, questions_per_song: parseInt(e.target.value) }
                })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': { color: '#9C27B0' }
                  }
                }}
              />
            </Grid>

            {/* Moods (multi-select with Select All) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{
                  color: '#B0B0B0',
                  '&.Mui-focused': { color: '#9C27B0' }
                }}>
                  Moods
                </InputLabel>
                <Select
                  multiple
                  value={formData.quiz_config.mood_ids || []}
                  onChange={(e) => {
                    const { value } = e.target;
                    const allIds = moods.map(m => m.mood_type_id);

                    if (value.includes('all')) {
                      const newSelected = areAllSelected(formData.quiz_config.mood_ids || [], moods, 'mood_type_id')
                        ? []
                        : allIds;
                      setFormData(prev => ({
                        ...prev,
                        quiz_config: {
                          ...prev.quiz_config,
                          mood_ids: newSelected
                        }
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        quiz_config: {
                          ...prev.quiz_config,
                          mood_ids: value
                        }
                      }));
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const mood = moods.find(m => m.mood_type_id === value);
                        return (
                          <Chip
                            key={value}
                            label={mood?.display_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9C27B0' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#9C27B0' },
                    '& .MuiSvgIcon-root': { color: '#B0B0B0' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1A1A1A',
                        border: '1px solid #333',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': { backgroundColor: '#333' },
                          '&.Mui-selected': {
                            backgroundColor: '#9C27B0',
                            '&:hover': { backgroundColor: '#7B1FA2' }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="all">
                    <em>{areAllSelected(formData.quiz_config.mood_ids || [], moods, 'mood_type_id') ? 'Deselect All' : 'Select All'}</em>
                  </MenuItem>
                  {moods.map((mood) => (
                    <MenuItem key={mood.mood_type_id} value={mood.mood_type_id}>
                      {mood.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Topics (multi-select with Select All) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{
                  color: '#B0B0B0',
                  '&.Mui-focused': { color: '#9C27B0' }
                }}>
                  Topics
                </InputLabel>
                <Select
                  multiple
                  value={formData.quiz_config.topic_ids || []}
                  onChange={(e) => {
                    const { value } = e.target;
                    const allIds = topics.map(t => t.topic_type_id);

                    if (value.includes('all')) {
                      const newSelected = areAllSelected(formData.quiz_config.topic_ids || [], topics, 'topic_type_id')
                        ? []
                        : allIds;
                      setFormData(prev => ({
                        ...prev,
                        quiz_config: {
                          ...prev.quiz_config,
                          topic_ids: newSelected
                        }
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        quiz_config: {
                          ...prev.quiz_config,
                          topic_ids: value
                        }
                      }));
                    }
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const topic = topics.find(t => t.topic_type_id === value);
                        return (
                          <Chip
                            key={value}
                            label={topic?.display_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9C27B0' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#9C27B0' },
                    '& .MuiSvgIcon-root': { color: '#B0B0B0' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1A1A1A',
                        border: '1px solid #333',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': { backgroundColor: '#333' },
                          '&.Mui-selected': {
                            backgroundColor: '#9C27B0',
                            '&:hover': { backgroundColor: '#7B1FA2' }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="all">
                    <em>{areAllSelected(formData.quiz_config.topic_ids || [], topics, 'topic_type_id') ? 'Deselect All' : 'Select All'}</em>
                  </MenuItem>
                  {topics.map((topic) => (
                    <MenuItem key={topic.topic_type_id} value={topic.topic_type_id}>
                      {topic.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Delay Seconds (Flash) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Delay Seconds (Insta)"
                type="number"
                value={formData.rapid_config.delay_seconds}
                onChange={(e) => setFormData({
                  ...formData,
                  rapid_config: { ...formData.rapid_config, delay_seconds: parseInt(e.target.value || '0', 10) }
                })}
                inputProps={{ min: 0, max: 120 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': { color: '#9C27B0' }
                  }
                }}
              />
            </Grid>

            {/* Quizlet Timer Seconds (Flash) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quizlet Timer Seconds (Insta)"
                type="number"
                value={formData.rapid_config.song_timer_seconds}
                onChange={(e) => setFormData({
                  ...formData,
                  rapid_config: { ...formData.rapid_config, song_timer_seconds: parseInt(e.target.value || '0', 10) }
                })}
                inputProps={{ min: 10, max: 600 }}
                helperText="Controls both quizlet timer and per-question timer"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': { color: '#9C27B0' }
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#888'
                  }
                }}
              />
            </Grid>

            {/* Schedule Times */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="datetime-local"
                value={formData.scheduled_start_time}
                onChange={(e) => setFormData({ ...formData, scheduled_start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  style: { color: '#fff' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' },
                    '& input[type="datetime-local"]': {
                      color: '#fff',
                      cursor: 'pointer',
                      '&::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                        cursor: 'pointer'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': { color: '#9C27B0' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="datetime-local"
                value={formData.scheduled_end_time}
                onChange={(e) => setFormData({ ...formData, scheduled_end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  style: { color: '#fff' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#9C27B0' },
                    '&.Mui-focused fieldset': { borderColor: '#9C27B0' },
                    '& input[type="datetime-local"]': {
                      color: '#fff',
                      cursor: 'pointer',
                      '&::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1)',
                        cursor: 'pointer'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: '#ccc',
                    '&.Mui-focused': { color: '#9C27B0' }
                  }
                }}
              />
            </Grid>

            {/* Info Alert */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  <strong>Insta Mode:</strong> No song selection needed. Songs will be pushed on-demand from the songs list or request compact.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          borderTop: '1px solid #333',
          p: 3,
          backgroundColor: '#1A1A1A',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
              resetForm();
            }}
            sx={{
              color: '#B0B0B0',
              border: '1px solid #333',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={createSession}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {editingSession ? 'Update' : 'Create'} Insta Quiz
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{
          color: '#fff',
          borderBottom: '1px solid #333',
          backgroundColor: '#1A1A1A',
          fontSize: '1.5rem',
          fontWeight: 600,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ViewIcon sx={{ mr: 2, color: '#9C27B0' }} />
            Session Details - {selectedSession?.quiz_name}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2, backgroundColor: '#1A1A1A' }}>
          {selectedSession && (() => {
            const config = typeof selectedSession.quiz_config === 'string'
              ? JSON.parse(selectedSession.quiz_config)
              : selectedSession.quiz_config;

            const rapidConfig = typeof selectedSession.rapid_config === 'string'
              ? JSON.parse(selectedSession.rapid_config)
              : selectedSession.rapid_config;

            return (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#9C27B0', mb: 2 }}>
                    Basic Information
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Quiz Name:</strong> {selectedSession.quiz_name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Status:</strong> {selectedSession.status}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Insta Status:</strong> {selectedSession.rapid_status || 'waiting'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Participants:</strong> {selectedSession.participant_count || 0}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#9C27B0', mb: 2 }}>
                    Configuration
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#121212', borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Difficulty:</strong> {(() => {
                        const difficultyId = config.difficulty_id || config.difficulty;
                        const difficulty = difficulties.find(d => d.difficulty_id === difficultyId);
                        return difficulty ? difficulty.level_name : (config.difficulty || 'Unknown');
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Questions per Song:</strong> {rapidConfig.questions_per_song || 3}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Delay Seconds (Insta):</strong> {typeof rapidConfig.delay_seconds === 'number' ? rapidConfig.delay_seconds : 10}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                      <strong>Quizlet Timer Seconds (Insta):</strong> {typeof rapidConfig.song_timer_seconds === 'number' ? rapidConfig.song_timer_seconds : 60}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                      <strong>Show Correct Answer:</strong> {rapidConfig.show_correct_answer ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{
          borderTop: '1px solid #333',
          p: 3,
          backgroundColor: '#1A1A1A'
        }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            sx={{
              color: '#B0B0B0',
              border: '1px solid #333',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog
        open={participantsDialogOpen}
        onClose={() => setParticipantsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{
          color: '#fff',
          borderBottom: '1px solid #333',
          display: 'flex',
          alignItems: 'center'
        }}>
          <PeopleIcon sx={{ mr: 1, color: '#00BCD4' }} />
          Participants - {selectedSession?.quiz_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {loadingParticipants ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress sx={{ color: '#9C27B0' }} />
              <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                Loading participants...
              </Typography>
            </Box>
          ) : (!participants || participants.length === 0) ? (
            <Box textAlign="center" py={4}>
              <PeopleIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#ccc', mb: 1 }}>
                No participants yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Participants will appear here when they join the flash quiz
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Score</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Position</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Joined At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.map((participant) => (
                    <TableRow key={participant.participant_id || Math.random()} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                      <TableCell sx={{ color: '#fff' }}>
                        {(() => {
                          const guest = (participant.guest_name || '').trim();
                          if (guest) return guest;
                          const fname = (participant.fname || '').trim();
                          const lname = (participant.lname || '').trim();
                          const combined = `${fname} ${lname}`.trim();
                          return combined || 'Registered User';
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={participant.user_id ? 'Registered' : 'Guest'}
                          size="small"
                          sx={{
                            backgroundColor: participant.user_id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                            color: participant.user_id ? '#4CAF50' : '#FF9800',
                            border: `1px solid ${participant.user_id ? '#4CAF50' : '#FF9800'}`,
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>
                        {participant.current_score || 0}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.current_position ? `#${participant.current_position}` : '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#ccc' }}>
                        {participant.joined_at ? new Date(participant.joined_at).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
          <Button
            onClick={() => setParticipantsDialogOpen(false)}
            sx={{ color: '#ccc' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quizlet Rankings Dialog */}
      <Dialog
        open={quizletRankingsDialogOpen}
        onClose={() => setQuizletRankingsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: '#1A1A1A', color: '#fff' }
        }}
      >
        <DialogTitle sx={{
          color: '#fff',
          borderBottom: '1px solid #333',
          backgroundColor: '#1A1A1A',
          fontSize: '1.5rem',
          fontWeight: 600,
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LeaderboardIcon sx={{ mr: 2, color: '#FF9800' }} />
            Quizlet Rankings - {selectedSession?.quiz_name}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, backgroundColor: '#1A1A1A' }}>
          {loadingQuizletRankings ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress sx={{ color: '#9C27B0' }} />
              <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                Loading quizlet rankings...
              </Typography>
            </Box>
          ) : (!quizletRankings || quizletRankings.length === 0) ? (
            <Box textAlign="center" py={4}>
              <LeaderboardIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#ccc', mb: 1 }}>
                No quizlet data available
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Quizlet rankings will appear here after users play the quiz
              </Typography>
            </Box>
          ) : (
            <Box>
              {quizletRankings.map((quizlet, index) => {
                // Determine quizlet status
                const quizletId = quizlet.question_id || `${quizlet.song_id || ''}_${quizlet.pushed_at || ''}`;
                const pushedQuizlet = pushedQuizlets.find(pq => pq.id === quizletId);
                const hasUsers = quizlet.users && quizlet.users.length > 0;
                const isRunning = pushedQuizlet?.status === 'running';
                const isCompleted = hasUsers && !isRunning; // Completed if has users but not running
                
                return (
                <Accordion
                  key={quizlet.question_id || index}
                  sx={{
                    backgroundColor: isCompleted
                      ? 'linear-gradient(90deg, rgba(34,197,94,0.10), rgba(15,23,42,0.9))'
                      : isRunning
                        ? 'linear-gradient(90deg, rgba(129,140,248,0.12), rgba(15,23,42,0.9))'
                        : '#121212',
                    border: '1px solid rgba(31, 41, 55, 0.9)',
                    borderRadius: '8px !important',
                    mb: 2,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: isCompleted ? 'rgba(34,197,94,0.5)' : isRunning ? 'rgba(129,140,248,0.6)' : '#9C27B0',
                      margin: '0 0 16px 0',
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#9C27B0' }} />}
                    sx={{
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                      },
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        my: 2
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          bgcolor: '#020617',
                          border: '1px solid rgba(31,41,55,0.9)',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                      >
                        {quizlet.song_image || quizlet.coverImage ? (
                          <img 
                            src={quizlet.song_image || quizlet.coverImage} 
                            alt={quizlet.song_name || quizlet.songName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#777',
                              fontSize: 12
                            }}
                          >
                            No Image
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: '#e5e7eb', 
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {quizlet.song_name || quizlet.songName || 'Unknown Song'}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#9ca3af',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {quizlet.artist_name || quizlet.artistName || 'Unknown Artist'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {isCompleted ? (
                          <Chip
                            size="small"
                            label="Completed"
                            sx={{
                              bgcolor: 'rgba(34,197,94,0.12)',
                              color: '#bbf7d0',
                              border: '1px solid rgba(22,163,74,0.5)',
                              fontWeight: 600
                            }}
                          />
                        ) : isRunning ? (
                          <Chip
                            size="small"
                            label="Running"
                            sx={{
                              bgcolor: 'rgba(129,140,248,0.15)',
                              color: '#e0e7ff',
                              border: '1px solid rgba(79,70,229,0.6)',
                              fontWeight: 600
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Expired"
                            sx={{
                              bgcolor: 'rgba(31,41,55,0.8)',
                              color: '#e5e7eb',
                              border: '1px solid rgba(55,65,81,0.9)',
                              fontWeight: 600
                            }}
                          />
                        )}
                        <Chip
                          label={`${quizlet.users?.length || 0} Players`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 152, 0, 0.2)',
                            color: '#FF9800',
                            border: '1px solid #FF9800',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: 'rgba(10,10,10,0.5)', p: 3 }}>
                    {(!quizlet.users || quizlet.users.length === 0) ? (
                      <Box textAlign="center" py={2}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          No users have played this quizlet yet
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Divider sx={{ mb: 2, borderColor: '#111827' }} />
                        <TableContainer component={Paper} sx={{ backgroundColor: '#121212', borderRadius: 2 }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Rank</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Score</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Time Taken</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {quizlet.users.map((user, userIndex) => (
                                <TableRow
                                  key={user.participant_id || userIndex}
                                  sx={{
                                    '&:hover': { backgroundColor: '#1A1A1A' },
                                    backgroundColor: userIndex < 3 ? `rgba(255, 215, 0, ${0.05 * (3 - userIndex)})` : 'transparent'
                                  }}
                                >
                                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {user.rank || userIndex + 1}
                                      {(user.rank || userIndex + 1) === 1 && '🥇'}
                                      {(user.rank || userIndex + 1) === 2 && '🥈'}
                                      {(user.rank || userIndex + 1) === 3 && '🥉'}
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ color: '#fff' }}>
                                    {(() => {
                                      const guest = (user.guest_name || '').trim();
                                      if (guest) return guest;
                                      const fname = (user.fname || user.first_name || '').trim();
                                      const lname = (user.lname || user.last_name || '').trim();
                                      const combined = `${fname} ${lname}`.trim();
                                      return combined || 'Player';
                                    })()}
                                  </TableCell>
                                  <TableCell sx={{ color: '#4CAF50', fontWeight: 600 }}>
                                    {user.score || user.total_score || 0}
                                  </TableCell>
                                  <TableCell sx={{ color: '#fff' }}>
                                    {user.time_taken ? (() => {
                                      // time_taken is stored in milliseconds, convert to seconds
                                      // If value is less than 100, it's likely already in seconds
                                      const timeInSeconds = user.time_taken >= 100 
                                        ? (user.time_taken / 1000).toFixed(2)
                                        : user.time_taken.toFixed(2);
                                      return `${timeInSeconds}s`;
                                    })() : '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={user.user_id ? 'Registered' : 'Guest'}
                                      size="small"
                                      sx={{
                                        backgroundColor: user.user_id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                                        color: user.user_id ? '#4CAF50' : '#FF9800',
                                        border: `1px solid ${user.user_id ? '#4CAF50' : '#FF9800'}`,
                                        fontWeight: 600
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          borderTop: '1px solid #333',
          p: 3,
          backgroundColor: '#1A1A1A'
        }}>
          <Button
            onClick={() => setQuizletRankingsDialogOpen(false)}
            sx={{
              color: '#B0B0B0',
              border: '1px solid #333',
              px: 3,
              py: 1,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </ManagementContainer>
  );
});

export default FlashQuizManagement;

